import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { __ } from '@app/shared/functions/object.functions';
import { MetricsService } from '@app/shared/services/metrics.service';
import { TranscriptService } from '@app/shared/services/transcript.service';
import { TranscriptCue, TranscriptInfo } from '@app/shared/models/classes/Transcript';
import {TranscriptViewComponent} from '@app/shared/components/transcript-view/transcript-view.component';
import { Subject, of } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { AudioPlayerService, Queue, QueueItem } from './audio-player.service';
import {AudioPlayerComponent} from '../audio-player/audio-player.component';

@Component({
    selector: 'hypecast-audio-player-control',
    templateUrl: './audio-player-control.component.html',
    styleUrls: ['./audio-player-control.component.scss'],
    imports: [AudioPlayerComponent, TranscriptViewComponent],
    providers: [MetricsService, TranscriptService],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AudioPlayerControlComponent implements OnInit, OnDestroy {

  queue!: Queue;

  currentItem!: QueueItem;

  isExternalSource: boolean = false;

  transcriptCues: TranscriptCue[] = [];
  transcriptCurrentMs: number = 0;
  showMiniTranscript: boolean = false;
  private loadedTranscriptEpisodeId: string | null = null;

  readonly IsNullOrUndefined = __.IsNullOrUndefined;

  private _destroy$?: Subject<void>;
  private get destroy$(): Subject<void> {
    return this._destroy$ ??= new Subject<void>();
  }

  constructor(
    private audioPlayerService: AudioPlayerService,
    private metricsService: MetricsService,
    private cdr: ChangeDetectorRef,
    private transcriptService: TranscriptService
  ) {}

  get hasTranscript(): boolean {
    return this.transcriptService.getOriginal(this.currentItem?.episode?.transcripts) !== null;
  }

  toggleMiniTranscript(): void {
    this.showMiniTranscript = !this.showMiniTranscript;
  }

  onTranscriptSeek(seconds: number): void {
    this.audioPlayerService.seekTo(seconds);
    this.transcriptCurrentMs = seconds * 1000;
  }

  private loadTranscript(): void {
    const episode = this.currentItem?.episode;
    if (!episode || episode.id === this.loadedTranscriptEpisodeId) {
      return;
    }
    const original = this.transcriptService.getOriginal(episode.transcripts);
    this.transcriptCues = [];
    this.loadedTranscriptEpisodeId = episode.id;
    if (!original) {
      return;
    }
    // Anonymous plugin DTOs carry the storage url; the authenticated DTO resolves it via metadata.
    const resolveUrl = (transcript: TranscriptInfo) =>
      transcript.url
        ? of(transcript.url)
        : this.transcriptService.getTranscriptMeta(episode.id, transcript.id).pipe(map((meta) => meta.url ?? null));

    this.transcriptService
      .loadCues(original, episode.transcripts, resolveUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe((cues) => {
        this.transcriptCues = cues;
        this.cdr.markForCheck();
      });
  }

  get episodeImageUrl(): string {
    const ep = this.currentItem?.episode;
    if (!ep) return '';
    return ep.imageLarge?.fileSource || ep.image?.fileSource || ep.externalImageLink || '';
  }

  get isCurrentlyPlaying(): boolean {
    return this.currentItem?.isPlaying === true;
  }

  togglePlayPause(): void {
    if (this.isExternalSource) {
      this.audioPlayerService.sendCommand({ type: this.isCurrentlyPlaying ? 'pause' : 'play' });
      return;
    }
    if (this.isCurrentlyPlaying) {
      this.audioPlayerService.pause();
    } else {
      this.audioPlayerService.play();
    }
  }

  ngOnInit() {
    this.audioPlayerService.externalSource$
      .pipe(takeUntil(this.destroy$))
      .subscribe((active) => {
        this.isExternalSource = active;
        this.cdr.markForCheck();
      });

    this.audioPlayerService.queue$
      .pipe(takeUntil(this.destroy$))
      .subscribe((queue: Queue) => {
        this.queue = queue;

        const currentItem = queue.items[0];

        if (!__.IsNullOrUndefined(this.currentItem) && !__.IsNullOrUndefined(currentItem) && this.currentItem.src !== currentItem.src) {
          this.currentItem = currentItem;
        } else {
          this.currentItem = currentItem;
        }
        this.loadTranscript();
        this.cdr.markForCheck();
      });

    this.audioPlayerService.isPlaying$
      .pipe(takeUntil(this.destroy$))
      .subscribe((item: QueueItem | null) => {
        if (!item) return;
        if (this.currentItem) {
          this.currentItem.isPlaying = item.isPlaying;
        }
        this.cdr.markForCheck();
      });

    this.audioPlayerService.progress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((progress) => {
        this.transcriptCurrentMs = (progress.position ?? 0) * 1000;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackStream($event: boolean): void {
    if ($event === false) {
      this.metricsService
        .createNewStream(this.currentItem.episode.id)
        .subscribe({
          next: (result: boolean) => { }
        });
    }
  }

  trackListen(value: number): void {
    if (value > 0) {
      this.metricsService
        .createNewListen(this.currentItem.episode.id, String(value))
        .subscribe({
          next: (result: boolean) => { }
        });
    }
  }
}
